import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  Stack,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Link,
  Tooltip,
  Card,
} from '@mui/material';
import {
  AlertTriangle,
  Check,
  ArrowLeft,
  ChevronRight,
  Search,
  ChevronDown,
  Wallet,
  Eye,
  FileText,
  CreditCard,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getSubscriptions,
  getProducts,
  createSubscription,
  getInvoices,
  initializePaystackPayment,
  verifyPaystackPayment,
} from '../api/subscriptions.api';

const ENABLE_WALLET = false;

export default function GlobalBillingPage() {
  const { user } = useSelector((state) => state.auth);
  const walletBalance = user?.walletBalance || 0;

  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search filter & pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const rowsPerPage = 5;

  // Modal State for Ordering Services
  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedAppId, setSelectedAppId] = useState('');
  const [domainName, setDomainName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Renewal Checkout Modal states
  const [openRenewModal, setOpenRenewModal] = useState(false);
  const [renewingSubscription, setRenewingSubscription] = useState(null);
  const [renewalMonths, setRenewalMonths] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [couponInputOpen, setCouponInputOpen] = useState(false);

  // Payment steps
  const [paymentStep, setPaymentStep] = useState(1); // 1 = review details, 2 = choose payment method, 3 = success
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Details Modal states (for Viewing Subscription / Invoice details)
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsType, setDetailsType] = useState('subscription'); // 'subscription' | 'invoice'

  const getStatusColor = (status) => {
    if (status === 'active' || status === 'paid') return 'success';
    if (status === 'pending-setup' || status === 'pending') return 'warning';
    if (status === 'failed' || status === 'abandoned') return 'error';
    return 'default';
  };

  const getDiscountPercent = (months) => {
    if (months === 3) return 5;
    if (months === 6) return 10;
    if (months === 12) return 15;
    return 0;
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // 1. Fetch user subscriptions
      const subRes = await getSubscriptions();
      if (subRes.ok && subRes.data?.success) {
        setSubscriptions(subRes.data.data || []);
      }

      // 2. Fetch all products
      const prodRes = await getProducts();
      if (prodRes.ok && prodRes.data?.success) {
        setProducts(prodRes.data.data || []);
      }

      // 3. Fetch invoices for history
      const invRes = await getInvoices();
      if (invRes.ok && invRes.data?.success) {
        setInvoices(invRes.data.data || []);
      }
    } catch (err) {
      toast.error('Error connecting to accounts billing services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const reference = query.get('reference') || query.get('trxref');
    if (reference) {
      const verifyPayment = async () => {
        setLoading(true);
        try {
          const res = await verifyPaystackPayment({ reference });
          if (res.ok && res.data?.success) {
            toast.success('Payment settled successfully via Paystack!');
          } else {
            const message = res.data?.message || 'Payment verification failed';
            if (res.data?.status === 'abandoned') {
              toast.warn('Payment was cancelled or abandoned.');
            } else {
              toast.error(message);
            }
          }
        } catch (err) {
          toast.error('An error occurred during payment verification');
        } finally {
          window.history.replaceState({}, document.title, window.location.pathname);
          fetchData();
        }
      };
      verifyPayment();
    } else {
      fetchData();
    }
  }, []);

  const handlePurchase = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    if (!selectedAppId) {
      toast.error('Please select an application workspace');
      return;
    }
    const selected = products.find((p) => p._id === selectedProduct);
    if (!selected) return;

    try {
      setSubmitting(true);

      const payload = {
        productId: selectedProduct,
        appId: selectedAppId,
        metadata: {
          domainName: selected.type === 'domain' ? domainName : undefined,
          phoneNumber: selected.type === 'whatsapp' ? phoneNumber : undefined,
          shortCode: selected.type === 'ussd' ? shortCode : undefined,
        },
      };

      const res = await createSubscription(payload);
      if (res.ok && res.data?.success) {
        toast.success('Subscription ordered successfully! Access invoice to activate.');
        setOpenModal(false);
        fetchData();
      } else {
        toast.error(res.data?.message || 'Failed to order subscription');
      }
    } catch (err) {
      toast.error('An error occurred while placing order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRenewModal = (sub) => {
    setRenewingSubscription(sub);
    setRenewalMonths(1);
    setPaymentStep(1);
    setCouponCode('');
    setAppliedDiscount(0);
    setCouponInputOpen(false);
    setIsProcessingPayment(false);
    setOpenRenewModal(true);
  };

  const handleOpenSubscriptionDetails = (sub) => {
    setDetailsData(sub);
    setDetailsType('subscription');
    setOpenDetailsModal(true);
  };

  const handleOpenInvoiceDetails = (inv) => {
    setDetailsData(inv);
    setDetailsType('invoice');
    setOpenDetailsModal(true);
  };

  const handleApplyCoupon = () => {
    if (couponCode.toUpperCase() === 'WELCOME10') {
      setAppliedDiscount(10);
      toast.success('Coupon applied successfully! Extra 10% Discount added.');
    } else {
      toast.error('Invalid coupon code. Try WELCOME10.');
    }
  };

  const calculateCheckoutTotal = () => {
    if (!renewingSubscription) {
      return {
        subtotal: 0,
        tierDiscountPercent: 0,
        tierDiscountAmount: 0,
        couponDiscountAmount: 0,
        taxes: 0,
        total: 0,
      };
    }
    const basePrice = renewingSubscription.product?.price || 0;

    let tierDiscountPercent = 0;
    if (renewalMonths === 3) tierDiscountPercent = 5;
    else if (renewalMonths === 6) tierDiscountPercent = 10;
    else if (renewalMonths === 12) tierDiscountPercent = 15;

    const subtotal = basePrice * renewalMonths;
    const tierDiscountAmount = Math.round((subtotal * tierDiscountPercent) / 100);

    const couponDiscountAmount = Math.round(((subtotal - tierDiscountAmount) * appliedDiscount) / 100);

    // VAT / Tax removed (flat amount)
    const total = subtotal - tierDiscountAmount - couponDiscountAmount;

    return {
      subtotal,
      tierDiscountPercent,
      tierDiscountAmount,
      couponDiscountAmount,
      taxes: 0,
      total,
    };
  };

  const handleExecutePayment = async () => {
    try {
      setIsProcessingPayment(true);
      const isMock = String(renewingSubscription._id).startsWith('mock');

      if (isMock) {
        setTimeout(() => {
          setIsProcessingPayment(false);
          setPaymentStep(3);
          const { total } = calculateCheckoutTotal();
          const durationDays = renewalMonths * 30;
          toast.success(`Payment of ₦${total.toLocaleString()} processed successfully via PAYSTACK (Mock)`);

          setSubscriptions((prev) =>
            prev.map((s) => {
              if (s._id === renewingSubscription._id) {
                return {
                  ...s,
                  daysLeft: (s.daysLeft || 0) + durationDays,
                  expiresAt: new Date(
                    new Date(s.expiresAt || Date.now()).getTime() + durationDays * 24 * 60 * 60 * 1000
                  ).toISOString(),
                };
              }
              return s;
            })
          );

          setInvoices((prev) => [
            {
              _id: `mock-inv-${Math.floor(Math.random() * 100000)}`,
              subscription: {
                product: { name: renewingSubscription.product?.name },
                app: renewingSubscription.app,
              },
              amount: total,
              dueDate: new Date().toISOString(),
              status: 'paid',
            },
            ...prev,
          ]);

          setTimeout(() => {
            setOpenRenewModal(false);
            fetchData();
          }, 2000);
        }, 2000);
        return;
      }

      const initRes = await initializePaystackPayment(renewingSubscription._id, {
        months: renewalMonths,
        couponCode,
        callbackUrl: `${window.location.origin}/dashboard/global-billing`,
      });

      if (!initRes.ok || !initRes.data?.success) {
        toast.error(initRes.data?.message || 'Failed to initialize payment transaction');
        setIsProcessingPayment(false);
        return;
      }

      const { authorization_url: authorizationUrl } = initRes.data.data;
      window.location.href = authorizationUrl;
    } catch (err) {
      toast.error('An error occurred during renewal checkout');
      setIsProcessingPayment(false);
    }
  };

  // Filter subscriptions by search query
  const filteredSubs = subscriptions.filter((sub) => {
    const q = searchQuery.toLowerCase();
    const prodName = (sub.product?.name || '').toLowerCase();
    const prodType = (sub.product?.type || '').toLowerCase();
    const appName = (sub.app?.name || (typeof sub.app === 'string' ? sub.app : '')).toLowerCase();
    return prodName.includes(q) || prodType.includes(q) || appName.includes(q);
  });

  // Pagination
  const indexOfLastRow = tablePage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredSubs.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (event, newPage) => {
    setTablePage(newPage);
  };

  // Unique apps for dropdown in ordering modal
  const uniqueAppMap = new Map();
  subscriptions.forEach((s) => {
    if (s.app && typeof s.app === 'object' && s.app._id) {
      uniqueAppMap.set(s.app._id, s.app.name || 'Workspace App');
    }
  });
  const availableApps = Array.from(uniqueAppMap.entries()).map(([id, name]) => ({ id, name }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fcfcfc', minHeight: '100vh', py: 2 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ letterSpacing: '-0.02em' }}>
            Accounts Billing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Consolidated invoices, subscriptions, and financial overview across all application workspaces.
          </Typography>
        </Box>

        {/* {products.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => {
              setSelectedProduct('');
              setSelectedAppId(availableApps[0]?.id || '');
              setOpenModal(true);
            }}
            sx={{
              mt: { xs: 2, sm: 0 },
              bgcolor: '#5d3cf2',
              color: '#ffffff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              py: 1,
              '&:hover': { bgcolor: '#4b2fd1' },
            }}
          >
            Order Feature Add-on
          </Button>
        )} */}
      </Stack>

      <Stack spacing={4}>
        {/* KPI Metrics Summary Grid */}
        <Grid container spacing={2.5}>
          {ENABLE_WALLET && (
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'primary.dark', color: 'white', borderRadius: 3, p: 2.5, height: '100%' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Wallet size={18} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Wallet Balance
                  </Typography>
                </Stack>
                <Typography variant="h4" fontWeight={800}>
                  ₦{walletBalance.toLocaleString()}
                </Typography>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={ENABLE_WALLET ? 3 : 4}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Active Subscriptions
              </Typography>
              <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mt: 0.5 }}>
                {subscriptions.filter((s) => s.status === 'active').length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={ENABLE_WALLET ? 3 : 4}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Pending Setup
              </Typography>
              <Typography variant="h4" fontWeight={800} color="warning.main" sx={{ mt: 0.5 }}>
                {subscriptions.filter((s) => s.status === 'pending-setup').length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={ENABLE_WALLET ? 3 : 4}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Unpaid Invoices
              </Typography>
              <Typography variant="h4" fontWeight={800} color="error.main" sx={{ mt: 0.5 }}>
                {invoices.filter((i) => i.status === 'pending').length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Subscriptions Table Grid wrapper */}
        <Paper
          variant="outlined"
          sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}
        >
          {/* Search Bar */}
          <Box sx={{ p: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <TextField
              placeholder="Search subscriptions or app..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setTablePage(1);
              }}
              InputProps={{
                startAdornment: <Search size={18} color="#999" style={{ marginRight: 8 }} />,
              }}
              sx={{ width: { xs: '100%', sm: 320 } }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#fafafa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }}>App Workspace</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }}>Subscription</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }}>Expiration date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }}>Auto-renewal</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }}>Renewal price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#666' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No active subscriptions found matching query.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentRows.map((sub) => {
                    const { daysLeft: subDaysLeft, expiresAt } = sub;
                    let daysLeft = 999;
                    if (subDaysLeft !== undefined) {
                      daysLeft = subDaysLeft;
                    } else if (expiresAt) {
                      daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                    }
                    const isExpiring = daysLeft <= 7;

                    return (
                      <TableRow key={sub._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        {/* App Workspace */}
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {sub.app?.name || (typeof sub.app === 'string' ? sub.app : 'N/A')}
                        </TableCell>

                        {/* Service Details */}
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {sub.product?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sub.product?.billingCycle ? `Billing: ${sub.product.billingCycle}` : 'Active feature'}
                          </Typography>
                        </TableCell>

                        {/* Expiry */}
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {sub.expiresAt ? new Date(sub.expiresAt).toISOString().split('T')[0] : 'N/A'}
                        </TableCell>

                        {/* Auto Renewal */}
                        <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>—</TableCell>

                        {/* Price */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            ₦{sub.product?.price ? sub.product.price.toLocaleString() : '0'}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            {isExpiring && (
                              <Chip
                                label="Expired"
                                color="error"
                                size="small"
                                icon={<AlertTriangle size={12} />}
                                sx={{ fontWeight: 700, height: 22, fontSize: '0.75rem' }}
                              />
                            )}
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenSubscriptionDetails(sub)}
                                sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <Eye size={18} />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleOpenRenewModal(sub)}
                              sx={{
                                bgcolor: '#5d3cf2',
                                color: '#ffffff',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: 1.5,
                                px: 2.5,
                                '&:hover': { bgcolor: '#4b2fd1' },
                              }}
                            >
                              Renew
                            </Button>
                            <IconButton size="small" onClick={() => handleOpenSubscriptionDetails(sub)}>
                              <ChevronRight size={20} color="#bbb" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          {filteredSubs.length > rowsPerPage && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
              <Pagination
                count={Math.ceil(filteredSubs.length / rowsPerPage)}
                page={tablePage}
                onChange={handlePageChange}
                shape="rounded"
              />
            </Box>
          )}
        </Paper>

        {/* Invoice Payment History Card */}
        <Paper
          variant="outlined"
          sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}
        >
          <Box sx={{ p: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="h6" fontWeight={700}>
              Payment History
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#fafafa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Invoice ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>App Workspace</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No invoice transactions logged.
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow key={inv._id} hover>
                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                        #{inv._id.substring(Math.max(0, inv._id.length - 8)).toUpperCase()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{inv.subscription?.app?.name || 'N/A'}</TableCell>
                      <TableCell>{inv.subscription?.product?.name || 'Service Activation'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>₦{inv.amount ? inv.amount.toLocaleString() : '0'}</TableCell>
                      <TableCell>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={(inv.status || 'PENDING').toUpperCase()}
                          color={getStatusColor(inv.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenInvoiceDetails(inv)}>
                          <Eye size={18} color="#666" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      {/* ─── Subscription / Invoice Details Modal ─── */}
      <Dialog open={openDetailsModal} onClose={() => setOpenDetailsModal(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontWeight: 800,
            borderBottom: '1px solid #f0f0f0',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {detailsType === 'subscription' ? (
              <FileText size={22} color="#5d3cf2" />
            ) : (
              <CreditCard size={22} color="#5d3cf2" />
            )}
            <Typography variant="h6" fontWeight={800}>
              {detailsType === 'subscription' ? 'Subscription Details' : 'Payment Invoice Details'}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setOpenDetailsModal(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {detailsData && detailsType === 'subscription' && (
            <Stack spacing={3}>
              {/* Product Header & Status */}
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fbfbff', border: '1px solid #e0e0e0' }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#1d1d1f">
                      {detailsData.product?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Workspace:{' '}
                      <strong>
                        {detailsData.app?.name || (typeof detailsData.app === 'string' ? detailsData.app : 'N/A')}
                      </strong>
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={(detailsData.status || 'ACTIVE').toUpperCase()}
                    color={getStatusColor(detailsData.status)}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </Paper>

              {/* Details Breakdown */}
              <Stack spacing={2} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Subscription ID
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                    #
                    {detailsData._id
                      ? detailsData._id.substring(Math.max(0, detailsData._id.length - 8)).toUpperCase()
                      : 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Pricing Cycle
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    ₦{detailsData.product?.price ? detailsData.product.price.toLocaleString() : '0'} /{' '}
                    {detailsData.product?.billingCycle || 'month'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Product Type
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                    {detailsData.product?.type || 'Standard'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Expiration Date
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {detailsData.expiresAt ? new Date(detailsData.expiresAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Auto-Renewal
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="text.secondary">
                    Disabled (Manual Renewal)
                  </Typography>
                </Stack>
              </Stack>

              {/* Metadata Configuration info if available */}
              {detailsData.metadata && Object.keys(detailsData.metadata).some((k) => detailsData.metadata[k]) && (
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 3, bgcolor: '#ffffff' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    Configuration & Metadata
                  </Typography>
                  <Stack spacing={1}>
                    {detailsData.metadata.domainName && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          Configured Domain
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {detailsData.metadata.domainName}
                        </Typography>
                      </Stack>
                    )}
                    {detailsData.metadata.phoneNumber && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          WhatsApp Number
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {detailsData.metadata.phoneNumber}
                        </Typography>
                      </Stack>
                    )}
                    {detailsData.metadata.shortCode && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          USSD ShortCode
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {detailsData.metadata.shortCode}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}

          {detailsData && detailsType === 'invoice' && (
            <Stack spacing={3}>
              {/* Invoice Header */}
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fbfbff', border: '1px solid #e0e0e0' }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#1d1d1f">
                      Invoice #
                      {detailsData._id
                        ? detailsData._id.substring(Math.max(0, detailsData._id.length - 8)).toUpperCase()
                        : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Service: <strong>{detailsData.subscription?.product?.name || 'Service Activation'}</strong>
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={(detailsData.status || 'PENDING').toUpperCase()}
                    color={detailsData.status === 'paid' ? 'success' : 'default'}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </Paper>

              {/* Invoice Breakdown */}
              <Stack spacing={2} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    App Workspace
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {detailsData.subscription?.app?.name || 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Invoice Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color="#1d1d1f">
                    ₦{detailsData.amount ? detailsData.amount.toLocaleString() : '0'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {detailsData.dueDate ? new Date(detailsData.dueDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Payment Gateway
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    Paystack (Card / USSD / Bank Transfer)
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #f0f0f0', px: 3, py: 2 }}>
          <Button onClick={() => setOpenDetailsModal(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Close
          </Button>
          {detailsType === 'subscription' && detailsData && (
            <Button
              variant="contained"
              onClick={() => {
                setOpenDetailsModal(false);
                handleOpenRenewModal(detailsData);
              }}
              sx={{
                bgcolor: '#5d3cf2',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                '&:hover': { bgcolor: '#4b2fd1' },
              }}
            >
              Renew Subscription
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ─── Subscription Renewal Checkout Dialog ─── */}
      <Dialog
        open={openRenewModal}
        onClose={() => !isProcessingPayment && setOpenRenewModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            borderBottom: '1px solid #f0f0f0',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {paymentStep === 2 && (
            <IconButton size="small" onClick={() => setPaymentStep(1)} disabled={isProcessingPayment}>
              <ArrowLeft size={20} />
            </IconButton>
          )}
          {paymentStep === 3
            ? 'Payment Confirmed'
            : `Renew ${renewingSubscription?.product?.name || 'Subscription'} (${
                renewingSubscription?.app?.name || 'Workspace'
              })`}
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {paymentStep === 1 && (
            <Box sx={{ p: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 2 }}>
                Review the details and proceed to checkout
              </Typography>

              {/* Period Dropdown Selection */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  border: '1px solid #4f46e5',
                  borderRadius: 3,
                  bgcolor: '#fbfbff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#4f46e5', fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 0.5 }}
                  >
                    Period
                  </Typography>
                  <Select
                    value={renewalMonths}
                    onChange={(e) => setRenewalMonths(e.target.value)}
                    variant="standard"
                    disableUnderline
                    IconComponent={ChevronDown}
                    sx={{
                      fontSize: '1.2rem',
                      fontWeight: 800,
                      color: '#1d1d1f',
                      '& .MuiSelect-select': { py: 0, pr: 3 },
                    }}
                  >
                    <MenuItem value={1}>1 month</MenuItem>
                    <MenuItem value={3}>3 months</MenuItem>
                    <MenuItem value={6}>6 months</MenuItem>
                    <MenuItem value={12}>12 months</MenuItem>
                  </Select>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center" textAlign="right">
                  {renewalMonths > 1 && (
                    <Box sx={{ bgcolor: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5', borderRadius: 1, px: 1, py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>
                        save {getDiscountPercent(renewalMonths)}%
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    {renewalMonths > 1 && (
                      <Typography
                        variant="caption"
                        sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}
                      >
                        ₦{((renewingSubscription?.product?.price || 0) * renewalMonths).toLocaleString()}
                      </Typography>
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1d1d1f' }}>
                      ₦{calculateCheckoutTotal().total.toLocaleString()}/mo
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Invoicing calculation breakdown */}
              <Stack spacing={2} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Expiration date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {renewingSubscription?.expiresAt
                      ? new Date(
                          new Date(renewingSubscription.expiresAt).setMonth(
                            new Date(renewingSubscription.expiresAt).getMonth() + renewalMonths
                          )
                        )
                          .toISOString()
                          .split('T')[0]
                      : 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {renewingSubscription?.product?.name} ({renewalMonths} {renewalMonths === 1 ? 'month' : 'months'})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    ₦{calculateCheckoutTotal().subtotal.toLocaleString()}
                  </Typography>
                </Stack>

                {/* Coupon Code Selector */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Coupon code
                  </Typography>
                  {couponInputOpen ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        placeholder="WELCOME10"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        sx={{
                          '& .MuiInputBase-input': { py: '4px', px: 1, fontSize: '0.8rem' },
                          width: 100,
                        }}
                      />
                      <Button size="small" onClick={handleApplyCoupon} sx={{ textTransform: 'none' }}>
                        Apply
                      </Button>
                    </Stack>
                  ) : (
                    <Link
                      component="button"
                      onClick={() => setCouponInputOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#4f46e5' }}
                    >
                      {appliedDiscount > 0 ? 'Coupon Active' : 'Add'}
                    </Link>
                  )}
                </Stack>

                {calculateCheckoutTotal().couponDiscountAmount > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="success.main">
                      Coupon Discount
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      -₦{calculateCheckoutTotal().couponDiscountAmount.toLocaleString()}
                    </Typography>
                  </Stack>
                )}


                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#1d1d1f' }}>
                    ₦{calculateCheckoutTotal().total.toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 3, color: 'text.secondary', textAlign: 'center', lineHeight: 1.5 }}
              >
                By checking out, you agree with our <Link href="#">Terms of Service</Link> and confirm that you have
                read our <Link href="#">Privacy Policy</Link>. You can cancel recurring payments at any time.
              </Typography>
            </Box>
          )}

          {paymentStep === 2 && (
            <Box sx={{ p: 4, bgcolor: '#f9f9fb' }}>
              <Grid container spacing={4}>
                {/* Left Side: Choose a payment method */}
                <Grid item xs={12} md={7.5}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 3, color: '#1d1d1f' }}>
                    Choose a payment method
                  </Typography>
                  <Stack spacing={2}>
                    <Accordion
                      expanded
                      variant="outlined"
                      sx={{ borderRadius: '12px !important', overflow: 'hidden', bgcolor: '#ffffff' }}
                    >
                      <AccordionSummary>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ width: '100%', pr: 2 }}
                        >
                          <Typography variant="body1" fontWeight={700}>
                            Paystack
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 1 }}>
                              Card / Transfer / USSD
                            </Typography>
                          </Stack>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ borderTop: '1px solid #f0f0f0', p: 3 }}>
                        {isProcessingPayment ? (
                          <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary">
                              Opening secure Paystack checkout portal...
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            You will complete your subscription renewal securely via Paystack using your Credit Card,
                            Bank Transfer, USSD, or QR code.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                </Grid>

                {/* Right Side Order Summary card */}
                <Grid item xs={12} md={4.5}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}
                  >
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2, color: '#1d1d1f' }}>
                      Order Summary
                    </Typography>
                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          {renewingSubscription?.product?.name} (renew)
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          ₦{calculateCheckoutTotal().subtotal.toLocaleString()}
                        </Typography>
                      </Stack>
                      {calculateCheckoutTotal().couponDiscountAmount > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="success.main">
                            Coupon Promo
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            -₦{calculateCheckoutTotal().couponDiscountAmount.toLocaleString()}
                          </Typography>
                        </Stack>
                      )}
                      <Divider />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body1" fontWeight={800}>
                          Total
                        </Typography>
                        <Typography variant="body1" fontWeight={800} color="#1d1d1f">
                          ₦{calculateCheckoutTotal().total.toLocaleString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {paymentStep === 3 && (
            <Stack alignItems="center" spacing={3} sx={{ py: 6, px: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={36} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  Renewal Processed!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 2 }}>
                  Your payment of <strong>₦{calculateCheckoutTotal().total.toLocaleString()}</strong> has been
                  finalized. Expiry has been extended by {renewalMonths * 30} days successfully.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #f0f0f0', px: 3, py: 2 }}>
          {paymentStep === 1 && (
            <>
              <Button onClick={() => setOpenRenewModal(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setPaymentStep(2)}
                sx={{
                  bgcolor: '#5d3cf2',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  '&:hover': { bgcolor: '#4b2fd1' },
                }}
              >
                Choose payment method
              </Button>
            </>
          )}

          {paymentStep === 2 && !isProcessingPayment && (
            <>
              <Button onClick={() => setPaymentStep(1)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleExecutePayment}
                sx={{
                  bgcolor: '#5d3cf2',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  '&:hover': { bgcolor: '#4b2fd1' },
                }}
              >
                Pay ₦{calculateCheckoutTotal().total.toLocaleString()}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal Dialog for ordering new service */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Order Feature Add-on</DialogTitle>
        <DialogContent>
          {availableApps.length > 0 && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Select App Workspace</InputLabel>
              <Select
                value={selectedAppId}
                label="Select App Workspace"
                onChange={(e) => setSelectedAppId(e.target.value)}
              >
                {availableApps.map((app) => (
                  <MenuItem key={app.id} value={app.id}>
                    {app.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth sx={{ mt: availableApps.length > 0 ? 1 : 2, mb: 3 }}>
            <InputLabel>Select Subscription Product</InputLabel>
            <Select
              value={selectedProduct}
              label="Select Subscription Product"
              onChange={(e) => setSelectedProduct(e.target.value)}
            >
              {products.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name} - ₦{p.price ? p.price.toLocaleString() : '0'} / {p.billingCycle || 'month'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {products.find((p) => p._id === selectedProduct)?.type === 'domain' && (
            <TextField
              label="Domain Name"
              placeholder="example.com"
              fullWidth
              value={domainName}
              onChange={(e) => setDomainName(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {products.find((p) => p._id === selectedProduct)?.type === 'whatsapp' && (
            <TextField
              label="WhatsApp Phone Number"
              placeholder="+2348000000000"
              fullWidth
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {products.find((p) => p._id === selectedProduct)?.type === 'ussd' && (
            <TextField
              label="USSD Telecom Carrier"
              placeholder="e.g. MTN Shared, Airtel"
              fullWidth
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePurchase} disabled={submitting}>
            {submitting ? 'Placing Order...' : 'Confirm Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
